import type { UPIData, UPIScheme } from "./types";

/**
 * Parse a UPI payment scheme string.
 *
 * Supports:
 * - `upi://pay?pa=merchant@upi&pn=Merchant+Name&am=100.00`
 * - Raw UPI IDs like `merchant@upi`
 *
 * Returns `null` if the input is not recognisable as UPI.
 */
export function parseUPIScheme(raw: string): UPIData | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Standard UPI URI scheme
  if (trimmed.toLowerCase().startsWith("upi://")) {
    try {
      const url = new URL(trimmed);
      const upiId = url.searchParams.get("pa") || undefined;
      if (!upiId) return null;

      return {
        type: "upi",
        raw: trimmed,
        upiId,
        merchantName: url.searchParams.get("pn")?.replace(/\+/g, " ") || undefined,
        amount: url.searchParams.get("am") || undefined,
        mcc: url.searchParams.get("mc") || undefined,
      };
    } catch {
      return null;
    }
  }

  // Raw UPI ID fallback (anything containing @)
  if (trimmed.includes("@")) {
    return {
      type: "upi",
      raw: trimmed,
      upiId: trimmed,
    };
  }

  return null;
}

/**
 * Extract all UPI query parameters from a raw UPI scheme string.
 * Useful if you need fields beyond the common ones (e.g. `mc`, `tr`, `tn`).
 */
export function parseUPISchemeFull(raw: string): UPIScheme | null {
  const trimmed = raw.trim();
  if (!trimmed.toLowerCase().startsWith("upi://")) return null;

  try {
    const url = new URL(trimmed);
    const get = (key: string) => url.searchParams.get(key) || undefined;
    return {
      pa: get("pa"),
      pn: get("pn"),
      am: get("am"),
      cu: get("cu"),
      tn: get("tn"),
      tr: get("tr"),
      url: get("url"),
      mc: get("mc"),
    };
  } catch {
    return null;
  }
}
